import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Icon,
  Loader,
  Message,
  Card,
  Table,
  Form,
  Input,
  Segment,
  Grid,
  Header,
  Checkbox,
  Label,
  Popup,
  Dropdown,
} from 'semantic-ui-react';
import { clientService } from '../../services/clients/ClientService';
import { ClientListResponse, ClientListFiltersRequest, ClienteLinhaDto } from '../../types/dtos';

function digitsOnlyPhone(s: string): string {
  return s.replace(/\D/g, '');
}

const QUALITY_COLOR: Record<string, 'green' | 'yellow' | 'red' | 'grey'> = {
  VERDE: 'green',
  AMARELO: 'yellow',
  VERMELHO: 'red',
};

const FLUXO_LABEL: Record<string, string> = {
  PENDENTE: 'Pendente',
  TEMPLATE_ENVIADO: 'Template enviado',
  AGUARDANDO_RESPOSTA: 'Aguardando',
  EM_FLUXO_AUTOMATIZADO: 'Em fluxo',
  ATENDIMENTO_HUMANO: 'Atendimento',
  FINALIZADO: 'Finalizado',
};

function QualidadeBadge({ valor }: { valor?: string | null }) {
  if (!valor) return <span style={{ color: '#aaa' }}>—</span>;
  const color = QUALITY_COLOR[valor] ?? 'grey';
  const emoji = valor === 'VERDE' ? '🟢' : valor === 'AMARELO' ? '🟡' : '🔴';
  return (
    <Label size="tiny" color={color}>
      {emoji} {valor}
    </Label>
  );
}

function LinhasBadge({ linhas, acessosAtivos }: { linhas?: ClienteLinhaDto[] | null; acessosAtivos?: number | null }) {
  if (acessosAtivos == null && (!linhas || linhas.length === 0)) {
    return <span style={{ color: '#aaa' }}>—</span>;
  }

  const count = linhas?.length ?? acessosAtivos ?? 0;

  if (!linhas || linhas.length === 0) {
    return <Label size="tiny" color="grey">{acessosAtivos} linha(s)</Label>;
  }

  const popupContent = (
    <div style={{ maxWidth: 320 }}>
      {linhas.map((l) => (
        <div key={l.numeroLinha} style={{ marginBottom: 6 }}>
          <strong>{l.numeroLinha}</strong>
          {l.temWhatsApp === true && (
            <Icon name="whatsapp" color="green" style={{ marginLeft: 4 }} />
          )}
          {l.temWhatsApp === false && (
            <Icon name="whatsapp" color="grey" style={{ marginLeft: 4 }} />
          )}
          <div style={{ fontSize: '0.82em', color: '#666' }}>
            {l.plano}{l.statusLinha ? ` · ${l.statusLinha}` : ''}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <Popup
      trigger={
        <Label size="tiny" color="blue" style={{ cursor: 'pointer' }}>
          <Icon name="mobile alternate" />{count} linha{count !== 1 ? 's' : ''}
        </Label>
      }
      content={popupContent}
      header={`${count} linha${count !== 1 ? 's' : ''} cadastrada${count !== 1 ? 's' : ''}`}
      on="hover"
      position="left center"
      wide
    />
  );
}

function FluxoBadge({ status }: { status?: string | null }) {
  if (!status) return <span style={{ color: '#aaa' }}>—</span>;
  const label = FLUXO_LABEL[status] ?? status;
  const colorMap: Record<string, 'grey' | 'blue' | 'teal' | 'olive' | 'orange' | 'green'> = {
    PENDENTE: 'grey',
    TEMPLATE_ENVIADO: 'blue',
    AGUARDANDO_RESPOSTA: 'teal',
    EM_FLUXO_AUTOMATIZADO: 'olive',
    ATENDIMENTO_HUMANO: 'orange',
    FINALIZADO: 'green',
  };
  return <Label size="tiny" color={colorMap[status] ?? 'grey'}>{label}</Label>;
}

/**
 * Lista os mesmos clientes do cadastro principal (GET /clients paged).
 * A rota legada /v1/whatsapp/clients não reflete esse cadastro.
 */
const WhatsAppClientsPage: React.FC = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<ClientListResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    search: '',
    situacaoCadastral: '',
    cidade: '',
    estado: '',
    temContrato: 'all' as 'all' | 'sim' | 'nao',
    temLinhasWhatsApp: 'all' as 'all' | 'sim' | 'nao',
    statusFluxo: '',
    qualidadeNumero: '',
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 1,
  });

  /** id do cliente → linha da lista (telefone + dados para variáveis do template na campanha) */
  const [selectedClients, setSelectedClients] = useState<Map<string, ClientListResponse>>(() => new Map());

  const loadClients = async (
    pageNumber: number,
    pageSize?: number,
    filterSnapshot?: typeof filters
  ) => {
    const size = pageSize ?? pagination.pageSize;
    const f = filterSnapshot ?? filters;
    setLoading(true);
    setError(null);
    try {
      const serverFilters: ClientListFiltersRequest = {
        search: f.search.trim() || undefined,
        situacaoCadastral: f.situacaoCadastral.trim() || undefined,
        cidade: f.cidade.trim() || undefined,
        estado: f.estado.trim() || undefined,
        statusFluxoWhatsApp: f.statusFluxo || undefined,
        qualidadeNumero: f.qualidadeNumero || undefined,
        pageNumber,
        pageSize: size,
      };
      if (f.temContrato === 'sim') serverFilters.hasContract = true;
      else if (f.temContrato === 'nao') serverFilters.hasContract = false;
      if (f.temLinhasWhatsApp === 'sim') serverFilters.hasLinhasWhatsApp = true;
      else if (f.temLinhasWhatsApp === 'nao') serverFilters.hasLinhasWhatsApp = false;

      const paged = await clientService.getClientsPaged(serverFilters);
      setClients(paged.items);
      setPagination({
        currentPage: paged.currentPage,
        pageSize: paged.pageSize,
        totalCount: paged.totalCount,
        totalPages: paged.totalPages,
      });
    } catch (err) {
      setError('Erro ao carregar clientes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const emptyFilters = {
    search: '',
    situacaoCadastral: '',
    cidade: '',
    estado: '',
    temContrato: 'all' as const,
    temLinhasWhatsApp: 'all' as const,
    statusFluxo: '',
    qualidadeNumero: '',
  };

  useEffect(() => {
    void loadClients(1, 20, emptyFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => void loadClients(1);

  const handleClearFilters = () => {
    setFilters(emptyFilters);
    void loadClients(1, pagination.pageSize, emptyFilters);
  };

  const allOnPageSelected =
    clients.length > 0 && clients.every((c) => selectedClients.has(c.id));

  const toggleSelectAllOnPage = useCallback(() => {
    setSelectedClients((prev) => {
      const next = new Map(prev);
      if (allOnPageSelected) {
        clients.forEach((c) => next.delete(c.id));
      } else {
        clients.forEach((c) => next.set(c.id, c));
      }
      return next;
    });
  }, [allOnPageSelected, clients]);

  const toggleSelectRow = useCallback((c: ClientListResponse) => {
    setSelectedClients((prev) => {
      const next = new Map(prev);
      if (next.has(c.id)) next.delete(c.id);
      else next.set(c.id, c);
      return next;
    });
  }, []);

  const handleCreateCampaignFromSelection = () => {
    const prefillClients = Array.from(selectedClients.values());
    const phones: string[] = [];
    const seen = new Set<string>();
    for (const c of prefillClients) {
      const d = digitsOnlyPhone(c.telefone1 ?? '');
      if (d.length > 0 && !seen.has(d)) {
        seen.add(d);
        phones.push(d);
      }
    }
    if (phones.length === 0) {
      setError('Nenhum telefone válido nos clientes selecionados.');
      return;
    }
    setError(null);
    navigate('/whatsapp/batches', { state: { prefillClients, phones } });
  };

  return (
    <div style={{ padding: '20px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          flexWrap: 'wrap',
          gap: '15px',
        }}
      >
        <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Icon name="users" />
          Clientes (WhatsApp)
        </h1>
        <Button type="button" onClick={() => navigate('/clients')}>
          <Icon name="external alternate" />
          Abrir cadastro completo (Clientes)
        </Button>
      </div>

      <Message info style={{ marginBottom: '20px' }}>
        <Message.Header>Mesma base do menu Clientes</Message.Header>
        <p>
          Esta lista usa o cadastro principal da Gettel. Para criar, editar ou fluxos avançados, use{' '}
          <strong>Clientes</strong> no menu lateral.
        </p>
        <p style={{ marginTop: '0.5em' }}>
          <strong>Ordem sugerida:</strong> 1) Selecione os clientes → 2) <strong>Criar campanha de disparo</strong> → 3)
          Na próxima tela, escolha o <strong>template</strong>: variáveis do modelo serão preenchidas pelo cadastro quando
          o nome da chave corresponder (CNPJ, razão social, etc.); payloads de botão precisam de JSON ou CSV.
        </p>
      </Message>

      <Card fluid style={{ marginBottom: '20px' }}>
        <Card.Content>
          <Form onSubmit={(e) => { e.preventDefault(); handleSearch(); }}>
            <Grid columns={4} stackable>
              <Grid.Column>
                <Form.Field>
                  <label>Buscar</label>
                  <Input
                    placeholder="CNPJ, razão social, e-mail..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    icon="search"
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column>
                <Form.Field>
                  <label>Situação cadastral</label>
                  <Input
                    placeholder="Ex.: ATIVA"
                    value={filters.situacaoCadastral}
                    onChange={(e) => setFilters({ ...filters, situacaoCadastral: e.target.value })}
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column>
                <Form.Field>
                  <label>Cidade</label>
                  <Input
                    placeholder="Ex.: Curitiba"
                    value={filters.cidade}
                    onChange={(e) => setFilters({ ...filters, cidade: e.target.value })}
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column>
                <Form.Field>
                  <label>UF</label>
                  <Input
                    placeholder="PR"
                    value={filters.estado}
                    onChange={(e) => setFilters({ ...filters, estado: e.target.value })}
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column>
                <Form.Field>
                  <label>Tem contrato</label>
                  <Dropdown
                    selection
                    fluid
                    value={filters.temContrato}
                    options={[
                      { key: 'all', value: 'all', text: 'Todos' },
                      { key: 'sim', value: 'sim', text: 'Sim' },
                      { key: 'nao', value: 'nao', text: 'Não' },
                    ]}
                    onChange={(_, d) =>
                      setFilters({ ...filters, temContrato: (d.value as 'all' | 'sim' | 'nao') ?? 'all' })
                    }
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column>
                <Form.Field>
                  <label>Tem linhas WhatsApp</label>
                  <Dropdown
                    selection
                    fluid
                    value={filters.temLinhasWhatsApp}
                    options={[
                      { key: 'all', value: 'all', text: 'Todos' },
                      { key: 'sim', value: 'sim', text: 'Sim' },
                      { key: 'nao', value: 'nao', text: 'Não' },
                    ]}
                    onChange={(_, d) =>
                      setFilters({ ...filters, temLinhasWhatsApp: (d.value as 'all' | 'sim' | 'nao') ?? 'all' })
                    }
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column>
                <Form.Field>
                  <label>Status Fluxo WA</label>
                  <Dropdown
                    selection
                    fluid
                    clearable
                    placeholder="Qualquer"
                    value={filters.statusFluxo || undefined}
                    options={[
                      { key: 'p', value: 'PENDENTE', text: 'Pendente' },
                      { key: 'te', value: 'TEMPLATE_ENVIADO', text: 'Template enviado' },
                      { key: 'ar', value: 'AGUARDANDO_RESPOSTA', text: 'Aguardando' },
                      { key: 'ef', value: 'EM_FLUXO_AUTOMATIZADO', text: 'Em fluxo' },
                      { key: 'ah', value: 'ATENDIMENTO_HUMANO', text: 'Atendimento' },
                      { key: 'f', value: 'FINALIZADO', text: 'Finalizado' },
                    ]}
                    onChange={(_, d) =>
                      setFilters({ ...filters, statusFluxo: (d.value as string) ?? '' })
                    }
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column>
                <Form.Field>
                  <label>Qualidade número</label>
                  <Dropdown
                    selection
                    fluid
                    clearable
                    placeholder="Qualquer"
                    value={filters.qualidadeNumero || undefined}
                    options={[
                      { key: 'v', value: 'VERDE', text: 'Verde' },
                      { key: 'a', value: 'AMARELO', text: 'Amarelo' },
                      { key: 'r', value: 'VERMELHO', text: 'Vermelho' },
                    ]}
                    onChange={(_, d) =>
                      setFilters({ ...filters, qualidadeNumero: (d.value as string) ?? '' })
                    }
                  />
                </Form.Field>
              </Grid.Column>
            </Grid>
            <div style={{ marginTop: '12px' }}>
              <Button.Group>
                <Button primary type="submit">
                  <Icon name="search" />
                  Buscar
                </Button>
                <Button type="button" onClick={handleClearFilters}>
                  <Icon name="erase" />
                  Limpar
                </Button>
              </Button.Group>
            </div>
          </Form>
        </Card.Content>
      </Card>

      {error && (
        <Message error style={{ marginBottom: '20px' }}>
          <Message.Header>Erro</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {loading ? (
        <Loader active size="large" content="Carregando clientes..." />
      ) : (
        <>
          <Card fluid>
            <Card.Content>
              <Card.Header>
                <Icon name="list" />
                Lista ({clients.length} nesta página)
              </Card.Header>
            </Card.Content>
            <Card.Content>
              {clients.length === 0 ? (
                <Segment placeholder>
                  <Header icon>
                    <Icon name="users" />
                    Nenhum cliente encontrado
                  </Header>
                  <Button type="button" onClick={() => navigate('/clients')}>
                    Ir para Clientes
                  </Button>
                </Segment>
              ) : (
                <Table celled striped>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell collapsing>
                        <Checkbox
                          checked={allOnPageSelected}
                          indeterminate={
                            !allOnPageSelected && clients.some((c) => selectedClients.has(c.id))
                          }
                          onChange={() => toggleSelectAllOnPage()}
                          title="Selecionar todos desta página"
                        />
                      </Table.HeaderCell>
                      <Table.HeaderCell>Empresa</Table.HeaderCell>
                      <Table.HeaderCell>CNPJ</Table.HeaderCell>
                      <Table.HeaderCell>Cidade / UF</Table.HeaderCell>
                      <Table.HeaderCell>Telefone</Table.HeaderCell>
                      <Table.HeaderCell>Situação</Table.HeaderCell>
                      <Table.HeaderCell>
                        <Icon name="mobile alternate" />
                        Linhas (WhatsApp)
                      </Table.HeaderCell>
                      <Table.HeaderCell>Qualidade</Table.HeaderCell>
                      <Table.HeaderCell>Fluxo WA</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {clients.map((c) => (
                      <Table.Row key={c.id}>
                        <Table.Cell collapsing>
                          <Checkbox
                            checked={selectedClients.has(c.id)}
                            onChange={() => toggleSelectRow(c)}
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <strong>{c.razaoSocial}</strong>
                          {c.nomeFantasia ? (
                            <div style={{ fontSize: '0.9em', color: '#666' }}>{c.nomeFantasia}</div>
                          ) : null}
                        </Table.Cell>
                        <Table.Cell>{c.cnpj}</Table.Cell>
                        <Table.Cell>
                          {c.cidade} / {c.estado}
                        </Table.Cell>
                        <Table.Cell>{c.telefone1}</Table.Cell>
                        <Table.Cell>{c.situacaoCadastral || '—'}</Table.Cell>
                        <Table.Cell collapsing>
                          <LinhasBadge linhas={c.linhas} acessosAtivos={c.acessosAtivos} />
                        </Table.Cell>
                        <Table.Cell collapsing>
                          <QualidadeBadge valor={c.qualidadeNumero} />
                        </Table.Cell>
                        <Table.Cell collapsing>
                          <FluxoBadge status={c.statusFluxoWhatsApp} />
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              )}
            </Card.Content>
          </Card>

          {selectedClients.size > 0 && (
            <Segment
              style={{
                marginTop: '16px',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                background: '#f0f7ff',
                borderColor: '#2185d0',
              }}
            >
              <span>
                <strong>{selectedClients.size}</strong> cliente(s) selecionado(s) (pode incluir outras páginas)
              </span>
              <Button.Group>
                <Button
                  type="button"
                  onClick={() => setSelectedClients(new Map())}
                >
                  Limpar seleção
                </Button>
                <Button
                  primary
                  type="button"
                  icon
                  labelPosition="left"
                  onClick={handleCreateCampaignFromSelection}
                >
                  <Icon name="paper plane" />
                  Criar campanha de disparo
                </Button>
              </Button.Group>
            </Segment>
          )}

          <div
            style={{
              marginTop: '14px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '10px',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ color: '#4a4a4a', fontSize: '0.9rem' }}>
              Total: <strong>{pagination.totalCount}</strong> cliente(s) — Página{' '}
              <strong>{pagination.currentPage}</strong> de <strong>{pagination.totalPages}</strong>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.85rem' }}>Itens por página</span>
              <select
                value={pagination.pageSize}
                onChange={async (e) => {
                  const next = Number(e.target.value);
                  setPagination((prev) => ({ ...prev, pageSize: next }));
                  await loadClients(1, next);
                }}
                style={{ height: '34px', borderRadius: '8px', border: '1px solid #ccc', padding: '0 8px' }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
                <option value={50}>50</option>
              </select>
              <Button
                size="small"
                disabled={pagination.currentPage <= 1}
                onClick={() => void loadClients(pagination.currentPage - 1)}
              >
                <Icon name="angle left" />
                Anterior
              </Button>
              <Button
                size="small"
                disabled={pagination.currentPage >= pagination.totalPages}
                onClick={() => void loadClients(pagination.currentPage + 1)}
              >
                Próxima
                <Icon name="angle right" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default WhatsAppClientsPage;
